import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  memo,
} from "react";
import axios from "axios";
import { observer } from "mobx-react-lite";
import { globalVars } from "../../global/GlobalVars.js";
import * as language from "../../utils/language.js";
import * as navigation from "../../utils/navigation.js";
import * as websocket from "../../utils/communication.js";
import * as XLSX from "xlsx";

//========================================================================
// Uhrzeit formattieren
export const formatDate = (dateString) => {
  const options = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };
  if (dateString === "") {
    return "";
  } else {
    return new Date(dateString).toLocaleDateString("de-DE", options);
  }
};

//========================================================================
// Konfiguration der api-Schnittstelle
const alarmUrl =
  window.location.port === "3000"
    ? `http://${window.location.hostname}:${globalVars.httpPort}/alarms/`
    : `/api/alarms/`;

//========================================================================
// nächste Alarme nachladen
const fetchNextArchivedAlarms = async (offset = 200, signal) => {
  try {
    const response = await axios.get(alarmUrl + "nextArchivedAlarms", {
      params: {
        offset: offset,
      },
      signal,
    });

    if (response.data.success) {
      return {
        alarms: response.data.alarms,
        nextOffset: response.data.nextOffset,
        hasMoreArchivedAlarms: response.data.hasMoreArchivedAlarms,
        error: null,
      };
    } else {
      return {
        alarms: [],
        nextOffset: offset,
        hasMoreArchivedAlarms: false,
        error: response.data.message || "Failed to fetch archived alarms",
      };
    }
  } catch (error) {
    if (axios.isCancel(error)) {
      return {
        alarms: [],
        nextOffset: offset,
        hasMoreArchivedAlarms: false,
        cancelled: true,
      };
    }
    return {
      alarms: [],
      nextOffset: offset,
      hasMoreArchivedAlarms: false,
      error:
        error.response?.data?.message ||
        "Network error while fetching archived alarms",
    };
  }
};

//========================================================================
// alle Alarme laden
const fetchAllArchivedAlarms = async (signal) => {
  try {
    const response = await axios.get(alarmUrl + "allArchivedAlarms", {
      signal,
    });
    return {
      alarms: response.data.alarms,
    };
  } catch (error) {
    if (axios.isCancel(error)) return {
        alarms: [],
        cancelled: true,
      };
    return { alarms: [] };
  }
};

//========================================================================
// Tabelle
const TableComponent = observer(({ style, scaleFactor, activeAlarms }) => {
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [archivedAlarmsOffset, setArchivedAlarmsOffset] = useState(200);
  const [hasMoreArchivedAlarms, setHasMoreArchivedAlarms] = useState(true);
  const [additionalAlarms, setAdditionalAlarms] = useState([]);

  const abortControllerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setSelectedRows([]);
      setAdditionalAlarms([]);
    };
  }, []);

  useEffect(() => {
    setSelectedRows([]);
    if (!activeAlarms) {
      setAdditionalAlarms([]);
      setArchivedAlarmsOffset(200);
      setHasMoreArchivedAlarms(true);
    }
  }, [activeAlarms]);
console.log(globalVars.archivedAlarms)
  //========================================================================
  // Alarmtyp übersetzen
  const alarmSeverity = useCallback((severity) => {
    if (severity === "alarm") {
      return language.displayText(["Alarm", "errore", "alarm"]);
    } else if (severity === "fault") {
      return language.displayText(["Störung", "blocco", "fault"]);
    } else if (severity === "warning") {
      return language.displayText(["Warnung", "avvertimento", "warning"]);
    }
  }, []);

  //========================================================================
  // Status des Alarms
  const alarmState = useCallback((state) => {
    if (state === 0) {
      return language.displayText([
        "Alarm nicht aktiv und quittiert",
        "allarme non attivo e confermato",
        "alarm not active and acknowledged",
      ]);
    } else if (state === 1) {
      return language.displayText([
        "Alarm aktiv",
        "allarme attivo",
        "alarm active",
      ]);
    } else if (state === 2) {
      return language.displayText([
        "Alarm aktiv und quittiert",
        "allarme attivo e confermato",
        "alarm active and acknowledged",
      ]);
    } else if (state === 3) {
      return language.displayText([
        "Alarm nicht aktiv",
        "allarme non attivo",
        "alarm not active",
      ]);
    }
  }, []);

  //========================================================================
  // Zeilenfarbe je nach Typ und Status
  const getRowColor = useCallback((severity, state, isSelected) => {
    let backgroundColor;
    if (severity === "alarm") {
      backgroundColor = state === 1 || state === 3 ? "#ff0000" : "#ffaaaa";
    } else if (severity === "fault") {
      backgroundColor = state === 1 || state === 3 ? "#e8ff2a" : "#f7ffa6";
    } else if (severity === "warning") {
      backgroundColor = state === 1 || state === 3 ? "#ff8b00" : "#ffd7a6";
    }
    return { backgroundColor: isSelected ? "#c0c0c0" : backgroundColor, cursor: "pointer" };
  }, []);

  //========================================================================
  // Weitere Alarme laden
  const loadMoreAlarms = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    fetchNextArchivedAlarms(archivedAlarmsOffset, abortControllerRef.current.signal)
      .then((result) => {
        if (result.cancelled || result.alarms.length === 0) return;

        setAdditionalAlarms((prev) => {
          const merged = [...prev, ...result.alarms];
          if (sortConfig.key !== null) {
            merged.sort((a, b) => {
              const aVal = a[sortConfig.key];
              const bVal = b[sortConfig.key];
              if (aVal === bVal) return 0;
              const cmp = aVal < bVal ? -1 : 1;
              return sortConfig.direction === "ascending" ? cmp : -cmp;
            });
          }
          return merged;
        });

        setArchivedAlarmsOffset(result.nextOffset);
        setHasMoreArchivedAlarms(result.hasMoreArchivedAlarms);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          // console.error("Error loading more alarms:", error);
        }
      });
  }, [archivedAlarmsOffset, sortConfig]);

  //========================================================================
  // Tabelle sortieren
  const sortedAlarms = useMemo(() => {
    const sourceAlarms = activeAlarms
      ? globalVars.activeAlarms
      : [...globalVars.archivedAlarms, ...additionalAlarms];

    if (sortConfig.key === null) return sourceAlarms;

    return [...sourceAlarms].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal === bVal) return 0;
      const cmp = aVal < bVal ? -1 : 1;
      return sortConfig.direction === "ascending" ? cmp : -cmp;
    });
  }, [
    globalVars.activeAlarms,
    globalVars.archivedAlarms,
    additionalAlarms,
    activeAlarms,
    sortConfig.key,
    sortConfig.direction,
  ]);

  const requestSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "ascending" ? "descending" : "ascending",
    }));
  }, []);

  //========================================================================
  // Zeile auswählen
  const toggleRowSelection = useCallback((index) => {
    setSelectedRows((prev) => {
      const isSelected = prev.includes(index);
      return isSelected ? prev.filter((i) => i !== index) : [...prev, index];
    });
  }, []);

  //========================================================================
  // Alarme quittieren
  const quittAlarm = useCallback(
    (quittMode) => {
      if (quittMode === "quittAll") {
        const action = activeAlarms ? "quittAllActAlarms" : "quittAllArchAlarms";
        websocket.quittAlarm(globalVars.ws, action);
      } else if (quittMode === "quittOne") {
        const selectedIndexes = selectedRows
          .map((selectedIndex) => {
            const alarm = sortedAlarms[selectedIndex];
            return alarm ? (activeAlarms ? alarm.id : alarm.idx) : null;
          })
          .filter(Boolean);

        if (selectedIndexes.length > 0) {
          const action = activeAlarms ? "quittActAlarm" : "quittArchAlarm";
          websocket.quittAlarm(globalVars.ws, action, selectedIndexes);
        }
      }
      setSelectedRows([]);
    },
    [activeAlarms, selectedRows, sortedAlarms],
  );

  //========================================================================
  // CSV Download
  const downloadCSV = useCallback((jsonArray, headers) => {
    try {
      const rows = jsonArray.map((item) => Object.values(item).join("\t"));
      const csv = [headers.join("\t"), ...rows].join("\n");
      const bom = "\uFEFF";
      const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = "alarm.csv";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
    } catch (err) {
      // console.error("CSV Download Fehler:", err);
    }
  }, []);

  // XLSX Download
  const downloadXLSX = useCallback((jsonArray, headers, headerKeys) => {
    try {
      const ws = XLSX.utils.json_to_sheet(jsonArray, { header: headerKeys });
      XLSX.utils.sheet_add_aoa(ws, [headers], { origin: "A1" });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Alarme");
      XLSX.writeFile(wb, "alarm.xlsx");
    } catch (err) {
      // console.error("XLSX Download Fehler:", err);
    }
  }, []);

  //========================================================================
  // Alarme downloaden
  const downloadAlarm = useCallback(
    async (mode) => {
      const abortController = new AbortController();
      try {
        const alarmsForDownload = activeAlarms
          ? globalVars.activeAlarms.slice()
          : (await fetchAllArchivedAlarms(abortController.signal)).alarms;

        if (!alarmsForDownload || alarmsForDownload.length === 0) return;

        const mapAlarm = (alarm) => ({
          severity: alarmSeverity(alarm.severity),
          text: language.displayText(alarm.text),
          raised: formatDate(alarm.raised),
          ...(activeAlarms ? {} : { cleared: formatDate(alarm.cleared) }),
          confirmed: formatDate(alarm.confirmed),
          state: alarmState(alarm.state),
        });

        const alarmsToDownload = alarmsForDownload.map(mapAlarm);

        const rowTitles = [
          language.displayText(["Typ", "tipo", "type"]),
          language.displayText(["Alarmtext", "testo allarme", "alarmtext"]),
          language.displayText(["Aktiv", "attivo", "raised"]),
          ...(!activeAlarms
            ? [language.displayText(["Deaktiv", "disattivo", "cleared"])]
            : []),
          language.displayText(["Quittiert", "confermato", "confirmed"]),
          language.displayText(["Status", "stato", "state"]),
        ];

        const headerKeys = [
          "severity",
          "text",
          "raised",
          ...(!activeAlarms ? ["cleared"] : []),
          "confirmed",
          "state",
        ];

        if (mode === "csv")  downloadCSV(alarmsToDownload, rowTitles);
        if (mode === "xlsx") downloadXLSX(alarmsToDownload, rowTitles, headerKeys);
      } catch (error) {
        if (!axios.isCancel(error)) {
          // console.error("Download error:", error);
        }
      } finally {
        abortController.abort();
      }
    },
    [activeAlarms, alarmSeverity, alarmState, downloadCSV, downloadXLSX],
  );

  //========================================================================
  // Spaltenbreite festlegen
  const columnStyles = useMemo(() => {
    const commonStyles = {
      textAlign: "center",
      border: "1px solid #ddd",
    };

    const textWidth = activeAlarms ? "62%" : "47%";
    return {
      severity: { width: "8%", ...commonStyles },
      text: {
        ...commonStyles,
        textAlign: "left",
        paddingLeft: "10px",
        width: textWidth,
      },
      raised: { width: "15%", ...commonStyles },
      cleared: { width: "15%", ...commonStyles },
      confirmed: { width: "15%", ...commonStyles },
    };
  }, [activeAlarms]);

  return (
    <div style={{ ...style }}>
      {/* ######################################################################################################## */}
      {/* ################################################ Spaltenname ########################################### */}
      <div
        style={{
          position: "absolute",
          width: "92.5%",
          height: "4%",
          top: "0px",
          alignItems: "start",
          display: "flex",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            height: `calc(${scaleFactor} * 40px)`,
          }}
        >
          <thead
            style={{
              cursor: "pointer",
              backgroundColor: "#636363",
              fontWeight: "bold",
              padding: "10px",
              userSelect: "none",
            }}
          >
            <tr>
              <th
                style={columnStyles.severity}
                onClick={() => requestSort("severity")}
              >
                {language.displayText(["Typ", "tipo", "type"])}
              </th>
              <th style={columnStyles.text} onClick={() => requestSort("text")}>
                {language.displayText([
                  "Alarmtext",
                  "testo allarme",
                  "alarmtext",
                ])}
              </th>
              <th
                style={columnStyles.raised}
                onClick={() => requestSort("raised")}
              >
                {language.displayText(["Aktiv", "attivo", "raised"])}
              </th>
              {!activeAlarms && (
                <th
                  style={columnStyles.cleared}
                  onClick={() => requestSort("cleared")}
                >
                  {language.displayText(["Deaktiv", "disattivo", "cleared"])}
                </th>
              )}
              <th
                style={columnStyles.confirmed}
                onClick={() => requestSort("confirmed")}
              >
                {language.displayText(["Quittiert", "confermato", "confirmed"])}
              </th>
            </tr>
          </thead>
        </table>
      </div>

      {/* ######################################################################################################## */}
      {/* ################################################ Alarme ################################################ */}
      <div
        className="scroll-container"
        style={{
          position: "absolute",
          width: "92.5%",
          height: `calc(100% - ${scaleFactor} * 40px)`,
          overflowY: "scroll",
          top: `calc(${scaleFactor} * 40px)`,
          scrollbarWidth: "none",
          backgroundColor: "lightgrey",
        }}
      >
        <style>
          {`
            .scroll-container::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
          }}
        >
          <tbody>
            {sortedAlarms.map((alarm, index) => (
              <tr
                key={index}
                style={{
                  ...getRowColor(
                    alarm.severity,
                    alarm.state,
                    selectedRows.includes(index),
                  ),
                  height: `calc(${scaleFactor} * 35px)`,
                }}
                onClick={() => toggleRowSelection(index)}
              >
                <td style={columnStyles.severity}>
                  {alarmSeverity(alarm.severity)}
                </td>
                <td style={columnStyles.text}>
                  {language.displayText(alarm.text)}
                </td>
                <td style={columnStyles.raised}>{formatDate(alarm.raised)}</td>
                {!activeAlarms && (
                  <td style={columnStyles.cleared}>
                    {formatDate(alarm.cleared)}
                  </td>
                )}
                <td style={columnStyles.confirmed}>
                  {formatDate(alarm.confirmed)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* ######################################################################################################## */}
      {/* ################################################ Buttons ############################################### */}
      <button
        style={{
          position: "absolute",
          top: "0%",
          right: "0%",
          height: `calc(${scaleFactor} * 35px)`,
          width: "7%",
          textAlign: "center",
          fontSize: `calc(${style.fontSize} * 0.8)`,
        }}
        onClick={() => quittAlarm("quittOne")}
      >
        {language.displayText(["Quittieren", "confermare", "confirm"])}
      </button>
      <button
        style={{
          position: "absolute",
          top: `calc(${scaleFactor} * 45px)`,
          right: "0%",
          height: `calc(${scaleFactor} * 35px)`,
          width: "7%",
          textAlign: "center",
          fontSize: `calc(${style.fontSize} * 0.8)`,
        }}
        onClick={() => quittAlarm("quittAll")}
      >
        {language.displayText(["Quitt. alle", "conf. tutti", "conf. all"])}
      </button>
      <button
        style={{
          position: "absolute",
          top: `calc(${scaleFactor} * 90px)`,
          right: "0%",
          height: `calc(${scaleFactor} * 35px)`,
          width: "7%",
          textAlign: "center",
          fontSize: `calc( ${style.fontSize} * 0.8)`,
        }}
        onClick={() => downloadAlarm("csv")}
      >
        {language.displayText(["Download", "download", "download"])}
      </button>
      <button
        style={{
          position: "absolute",
          top: `calc(${scaleFactor} * 135px)`,
          right: "0%",
          height: `calc(${scaleFactor} * 35px)`,
          width: "7%",
          textAlign: "center",
          fontSize: `calc(${style.fontSize} * 0.8)`,
        }}
        onClick={() => downloadAlarm("xlsx")}
      >
        {language.displayText(["Excel", "Excel", "Excel"])}
      </button>
      {!activeAlarms && (
        <div>
          <button
            style={{
              position: "absolute",
              bottom: `calc(${scaleFactor} * 80px)`,
              right: "0%",
              height: `calc(${scaleFactor} * 70px)`,
              width: "7%",
              textAlign: "center",
              fontSize: `calc(${style.fontSize} * 0.8)`,
            }}
            onClick={() =>
              navigation.changePage("custom", "__system/alarmConfig")
            }
          >
            {language.displayText([
              "Alarm- \nkonfiguration",
              "configurazione- \nallarme",
              "Alarm- \nconfiguration",
            ])}
          </button>

          {hasMoreArchivedAlarms && (
            <button
              style={{
                position: "absolute",
                bottom: "0%",
                right: "0%",
                height: `calc(${scaleFactor} * 70px)`,
                width: "7%",
                textAlign: "center",
                fontSize: `calc(${style.fontSize} * 0.8)`,
              }}
              onClick={loadMoreAlarms}
            >
              {language.displayText([
                "weitere Alarme \nladen",
                "load \nmore",
                "load \nmore",
              ])}
            </button>
          )}
        </div>
      )}
    </div>
  );
});

export default TableComponent;
